import { useMutation } from '@tanstack/react-query';
import { postJson, ApiError } from '../lib/api';
import { setAccessToken, setRefreshToken, setTenantId } from '../lib/auth';

interface LoginResponse {
  message: string;
  email: string;
}

export interface TenantInfo {
  tenantId: string;
  displayName: string;
  role: string;
  status?: string;
}

interface VerifyResponse {
  accessToken: string;
  idToken: string;
  refreshToken: string;
  expiresIn: number;
  user: {
    email: string;
    name: string;
    role: string;
  };
  tenants: TenantInfo[];
  selectedTenant: {
    tenantId: string;
    displayName: string;
    role: string;
  } | null;
}

interface SelectTenantResponse {
  selectedTenant: {
    tenantId: string;
    displayName: string;
    role: string;
  };
}

interface RefreshResponse {
  accessToken: string;
  idToken: string;
}

export interface LoginError {
  error: string;
  message: string;
}

export function useRequestLogin() {
  return useMutation({
    mutationFn: async (email: string) => {
      try {
        return await postJson<LoginResponse>('/api/auth/request-login', { email });
      } catch (err) {
        if (err instanceof ApiError && err.status === 422) {
          // NO_ACCOUNT error — re-throw with the body so the UI can show a proper message
          throw err;
        }
        throw err;
      }
    },
  });
}

export function useVerifyToken() {
  return useMutation({
    mutationFn: (token: string) =>
      postJson<VerifyResponse>('/api/auth/verify-token', { token }),
    onSuccess: (data) => {
      // Store JWT tokens (idToken has email claim, use it as access token)
      setAccessToken(data.idToken);
      setRefreshToken(data.refreshToken);

      // Auto-select tenant if only one
      if (data.selectedTenant) {
        setTenantId(data.selectedTenant.tenantId);
      }
    },
  });
}

export function useSelectTenant() {
  return useMutation({
    mutationFn: (tenantId: string) =>
      postJson<SelectTenantResponse>('/api/auth/select-tenant', { tenantId }),
    onSuccess: (data) => {
      setTenantId(data.selectedTenant.tenantId);
    },
  });
}

export function useRefreshToken() {
  return useMutation({
    mutationFn: (refreshToken: string) =>
      postJson<RefreshResponse>('/api/auth/refresh', { refreshToken }),
    onSuccess: (data) => {
      setAccessToken(data.idToken);
    },
  });
}
